/* globals FB */
import $ from 'jquery'
import has from 'lodash/has'

import store from 'base/store'
import SiteLocation from 'base/constants/site_location'
import { resetUserAndAddresses, fetchCart } from 'base/actions/asynchronous'
import { receiveInitialSession, updateVisitor } from 'base/actions/synchronous'

const Facebook = {
  Error: {
    INSUFFICIENT_PERMISSIONS: `
      You did not grant permission to use your
      email address. Please grant permission by
      connecting with Facebook again.
    `,
  },

  hasGrantedPermissions() {
    /*
     * Does the manual labor of checking
     * if the user has granted us permission
     * to use their email address.
     */
    const deferred = $.Deferred()

    FB.api('/me/permissions', permResponse => {
      if (!permResponse.error) {
        const emailPermission = permResponse.data.find(entry => entry.permission === 'email')
        deferred.resolve(emailPermission.status !== 'declined')
      } else {
        deferred.reject()
      }
    })

    return deferred
  },

  login({ siteLocation = SiteLocation.GENERIC_FACEBOOK, extraAuthParams = {} } = {}) {
    if (!this.deferred || this.deferred.state() !== 'pending') {
      this.deferred = $.Deferred()
    }

    if (typeof FB === 'undefined') {
      return this.deferred.reject(this._formatError('An error occured. Please try again.'))
    }

    /*
      We need this `auth_type` parameter if the user has previously authed
      but did not grant access to all the permissions we require. This flag
      will change the FB dialog to just say "hey, allow this one permission!"

      BUT BUT BUT, the FB docs suggest only passing this if the user needs
      to accept permissions they previously denied. In order to do that,
      we need to make another ajax call to `hasGrantedPermissions` then
      set `auth_type` accordingly but in doing that, we add a level of
      indirection to the click handler of the login button, which will cause
      the pop up to be blocked.

      So for now we are just always going to send `auth_type: rerequest`
      because it seems to always do the right, prompt for full perms the first
      time and only the denied perms the subsequent times. Keep an eye on this.
    */
    let params = {
      scope: 'email,public_profile,user_birthday,user_location',
      auth_type: 'rerequest',
    }

    params = Object.assign({}, extraAuthParams, params, {
      site_location: siteLocation,
    })

    FB.login(response => {
      if (response.authResponse) {
        /*
          There is a chance that the user has connected via Facebook
          but didn't grant all the required permissions we ask for (e.g. email).
          So before we hit our server with a sign in/up request, we ensure that
          the user has granted all the necessary permissions. If they did not,
          then fail, otherwise, sign them in!
        */
        this.hasGrantedPermissions().then(granted => {
          if (granted) {
            return $.post('/auth/facebook/callback', params)
              .done(resp => {
                store.dispatch(receiveInitialSession(resp.session))
                store.dispatch(updateVisitor(resp.session.visitor))
                store.dispatch(fetchCart(resp.session.cart))
                store.dispatch(resetUserAndAddresses(resp.session.user))

                this.deferred.resolve(resp)
              })
              .fail(resp => this.deferred.reject(this._formatError(resp)))
          } else {
            this.deferred.reject(this._formatError(this.Error.INSUFFICIENT_PERMISSIONS))
          }
        })
      } else {
        this.deferred.reject(
          this._formatError('The Facebook login window was closed. Please try again.'),
        )
      }
    }, params)

    return this.deferred
  },

  _formatError(err) {
    let message
    if (typeof err === 'object' && has(err, 'responseJSON')) {
      message = err.responseJSON.error
    } else if (typeof err === 'string') {
      message = err
    }

    return { responseJSON: { message } }
  },
}

export default Facebook
